import { auth } from './src/services/firebase'; // Import auth from your firebase setup file
import React, { useEffect, useState } from 'react';
import { View, Text, Alert } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { Ionicons } from '@expo/vector-icons';
import { Provider as PaperProvider } from 'react-native-paper';
import FlashMessage from 'react-native-flash-message';

// Firebase
import { onAuthStateChanged } from 'firebase/auth';
import { SafeAreaProvider } from 'react-native-safe-area-context';

// Screens
import LoginScreen from './src/screens/auth/LoginScreen';
import OnboardingScreen from './src/screens/auth/OnboardingScreen';
import TentativePlanScreen from './src/screens/auth/TentativePlanScreen';
import DashboardScreen from './src/screens/main/DashboardScreen';
import WorkoutPlanScreen from './src/screens/main/WorkoutPlanScreen';
import DietPlanScreen from './src/screens/main/DietPlanScreen';
import MacroTrackerScreen from './src/screens/main/MacroTrackerScreen';
import CheckInScreen from './src/screens/main/CheckInScreen';
import ChatScreen from './src/screens/main/ChatScreen';

// Services
import { AuthService } from './src/services/ApiService';
import PlanSyncService from './src/services/PlanSyncService';

// Firebase is initialized in src/services/firebase and exported as `auth`

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();

function MainTabs() {

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;

          if (route.name === 'Home') {
            iconName = focused ? 'home' : 'home-outline';
          } else if (route.name === 'Workout') {
            iconName = focused ? 'barbell' : 'barbell-outline';
          } else if (route.name === 'Diet') {
            iconName = focused ? 'restaurant' : 'restaurant-outline';
          } else if (route.name === 'Macros') {
            iconName = focused ? 'pie-chart' : 'pie-chart-outline';
          } else if (route.name === 'CheckIn') {
            iconName = focused ? 'checkmark-circle' : 'checkmark-circle-outline';
          } else if (route.name === 'Chat') {
            iconName = focused ? 'chatbubble' : 'chatbubble-outline';
          }

          return <Ionicons name={iconName} size={size} color={color} />;
        },
        tabBarActiveTintColor: '#3B82F6',
        tabBarInactiveTintColor: '#6D759E',
        tabBarStyle: {
          backgroundColor: '#161C2D',
          borderTopWidth: 1,
          borderTopColor: '#2C365E',
          paddingTop: 8,
          paddingBottom: 18,
          height: 70,
          shadowColor: '#000',
          shadowOffset: {
            width: 0,
            height: -2,
          },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 8,
        },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '500',
          marginTop: 4,
        },
        headerShown: false,
      })}>
      <Tab.Screen name="Home" component={DashboardScreen} />
      <Tab.Screen name="Workout" component={WorkoutPlanScreen} />
      <Tab.Screen name="Diet" component={DietPlanScreen} />
      <Tab.Screen name="Macros" component={MacroTrackerScreen} />
      <Tab.Screen
        name="CheckIn"
        component={CheckInScreen}
      />
      <Tab.Screen
        name="Chat"
        component={ChatScreen}
      />
    </Tab.Navigator>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Function to refresh user data after a successful login
  const refreshUserData = async () => {
    console.log('🔄 Refreshing user data...');
    const firebaseUser = auth.currentUser;
    if (firebaseUser) {
      try {
        const userData = await AuthService.verifyToken(firebaseUser);
        console.log('✅ User data refreshed:', userData.onboardingCompleted);
        setUser(userData);
        setNeedsOnboarding(!userData.onboardingCompleted);

        // Initialize plan sync if user has completed onboarding
        if (userData.onboardingCompleted) {
          console.log('🔄 Initializing plan sync...');
          PlanSyncService.initialize().then(result => {
            if (result.success) {
              console.log('✅ Plan sync initialized:', result.hasChanges ? 'Updates found' : 'Up to date');
            } else {
              console.warn('⚠️ Plan sync initialization failed:', result.message);
            }
          }).catch(error => {
            console.error('❌ Plan sync error:', error);
          });
        }
      } catch (error) {
        console.error('Auth verification error:', error);
        setUser({
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || 'User',
          onboardingCompleted: false,
        });
        setNeedsOnboarding(true);
      }
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? `User: ${firebaseUser.email}` : 'No user');
      if (firebaseUser) {
        // User is signed in
        await refreshUserData();
      } else {
        // User is signed out
        setUser(null);
        setNeedsOnboarding(false);
      }
      setLoading(false);
    });

    // Cleanup the listener when the component unmounts
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#0B0E14' }}>
        <Text style={{ color: '#FFFFFF' }}>Loading...</Text>
      </View>
    );
  }

  return (
    <SafeAreaProvider>
      <PaperProvider>
        <NavigationContainer>
          <Stack.Navigator screenOptions={{ headerShown: false }}>
            {!user ? (
              <Stack.Screen name="Login" component={LoginScreen} />
            ) : needsOnboarding ? (
              <Stack.Group>
                <Stack.Screen name="Onboarding">
                  {(props) => <OnboardingScreen {...props} onOnboardingComplete={refreshUserData} />}
                </Stack.Screen>
                <Stack.Screen name="TentativePlan">
                  {(props) => <TentativePlanScreen {...props} onOnboardingComplete={refreshUserData} />}
                </Stack.Screen>
              </Stack.Group>
            ) : (
              <Stack.Screen name="Main" component={MainTabs} />
            )}
          </Stack.Navigator>
        </NavigationContainer>
        <FlashMessage position="top" />
      </PaperProvider>
    </SafeAreaProvider>

  );
}
